import ReactFlow, { Background, Controls, MiniMap, useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
// import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Stack,
  IconButton,
  CircularProgress,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { addScenario } from "services/api";
import SnackbarWrapper from "components/Snackbar";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/authContext";
import CustomJobNode from "layouts/admin/Organization/components/JobView/components/CustomJobNode";
import AddIcon from "@mui/icons-material/Add";
import MDButton from "components/MDButton";
import AddScenarioJobDialog from "components/AddScenarioJobDialog";
import {
  addScenarioJob,
  getAllRevenueGeneratorsSimulation,
  updateScenarioJobDetails,
} from "services/api";
import { deleteScenarioJob } from "services/api";
import AnimatedNumber from "components/AnimatedNumber";
import { InsightCard, RenderRegexContent, RenderBulletList } from "services/utils";

import PropTypes from "prop-types";
import { getScenarioOverviewExport } from "services/api";
import { Download, FilterAltOff, UnfoldLess, UnfoldMore } from "@mui/icons-material";
// import { deleteScenarioEmployee } from "services/api";
import { manpowerPlanningAI } from "services/api";
import {
  AutoAwesome as AiIcon,
  ThumbUp as StrengthIcon,
  ThumbDown as WeaknessIcon,
  TipsAndUpdates as SuggestionIcon,
} from "@mui/icons-material";
import { scenarioTypes } from "services/utils";
import AiUpdateButton from "layouts/admin/StrategicInfo/components/AIIconButton";
import { cloneJobforScenario } from "services/api";

function ScenarioPage({
  scenario,
  setScenario,
  jobs,
  setJobs,
  // revenueGeneratorsSim,
  // setRevenueGeneratorsSim,
  triggerParentRefresh,
}) {
  const { t } = useTranslation();
  const snackbarRef = useRef();
  const { fitView, setCenter, getNodes } = useReactFlow();
  const [visibleBubbles, setVisibleBubbles] = useState({
    strengths: true,
    weaknesses: true,
    suggestions: true,
  });
  const [aiMessage, setAIMessage] = useState();
  const [openAddJobDialog, setOpenAddJobDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [buttonsLoading, setButtonsLoading] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [budgetAfterCalculation, setBudgetAfterCalculation] = useState(0);
  const [spendings, setSpendings] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [rfInstance, setRfInstance] = useState(null);
  const hasFitViewRun = useRef(false);
  // Format budget nicely with commas
  const formatNumber = (value) =>
    value !== null && value !== undefined && value !== ""
      ? new Intl.NumberFormat().format(value)
      : "";

  const handleEdit = (field, currentValue) => {
    setEditingField(field);
    setTempValue(currentValue || "");
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue("");
  };

  const handleDeleteJob = async (id) => {
    const res = await deleteScenarioJob(id);
    if (res != "error") {
      setJobs((prev) => prev.filter((j) => j.id !== id));
    }
  };

  useEffect(() => {
    if (aiMessage) {
      setVisibleBubbles({ strengths: true, weaknesses: true, suggestions: true });
    }
  }, [aiMessage]);

  const handleCloseBubble = (key) => {
    setVisibleBubbles((prev) => ({ ...prev, [key]: false }));
  };

  const fetchRevenueGenerators = async () => {
    try {
      // ✅ Only proceed if we have a scenario and jobs
      if (scenario?.id && jobs && jobs.length > 0) {
        const jobIds = jobs.map((job) => job.id);
        const revGenRes = await getAllRevenueGeneratorsSimulation(jobIds);

        if (revGenRes !== "error") {
          setRevenueGeneratorsSim(revGenRes);
        } else {
          console.error("Failed to fetch revenue generators");
        }
      } else {
        console.warn("No valid scenario or jobs found. Skipping fetch.");
      }
    } catch (error) {
      console.error("Error fetching revenue generators:", error);
    }
  };

  // A hook that returns a value only after a delay
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  // Parent: handles adding or updating jobs in the list
  const handleAddJob = async (jobData, outerDialog, isClone = false) => {
    try {
      let res;

      // --- 1. CLONE MODE ---
      if (isClone) {
        res = await cloneJobforScenario(
          jobData.targetScenarioId,
          jobData.sourceJobId,
          jobData.sourceType,
          jobData.targetManagerId,
          jobData.numberOfEmployees,
          jobData.salary
        );
      }
      // --- 2. EDIT MODE ---
      else if (Boolean(jobData.id) && outerDialog) {
        res = await updateScenarioJobDetails(jobData.id, {
          jobName: jobData.jobName,
          salary: jobData.salary,
          numberOfEmployees: jobData.numberOfEmployees,
          reportingToSolid: jobData.reportingToSolid,
        });
      }
      // --- 3. CREATE MODE ---
      else {
        res = await addScenarioJob(jobData);
      }

      // --- HANDLE RESPONSE ---
      if (res && res !== "error") {
        const exists = jobs.some((j) => j.id === res.id);

        // Always trigger full refresh for clones (to get sub-entities like FTEs)
        // or updates to existing jobs
        if (exists || isClone) {
          triggerParentRefresh();
        } else {
          setJobs((prev) => [...prev, res]);
        }

        // Determine Success Message
        let successMsg = "user.scenarios.job_add_success";
        if (isClone)
          successMsg = "Job cloned successfully"; // Add translation key if needed
        else if (jobData.id) successMsg = "user.scenarios.job_update_success";

        snackbarRef.current?.showSnackbar(t(successMsg), "success");
      } else {
        // Handle explicit "error" return or null
        throw new Error("API Error");
      }
    } catch (err) {
      console.error(err);
      snackbarRef.current?.showSnackbar(t("user.scenarios.job_add_failed"), "error");
    }
  };

  const handleSave = async () => {
    setButtonsLoading(true);
    const oldValue = scenario[editingField]; // store old value for rollback
    const updatedScenario = { ...scenario, [editingField]: tempValue };

    // Optimistically update UI
    setScenario(updatedScenario);
    try {
      const res = await addScenario(updatedScenario);

      if (res === "error") {
        // Rollback to old value if backend fails
        setScenario({ ...scenario, [editingField]: oldValue });
        snackbarRef.current?.showSnackbar(t("user.scenarios.update_failed"), "error");
      } else {
        snackbarRef.current?.showSnackbar(t("user.scenarios.update_success"), "success");
      }
    } catch (err) {
      // Rollback on exception
      setScenario({ ...scenario, [editingField]: oldValue });
      snackbarRef.current?.showSnackbar(t("user.scenarios.update_failed"), "error");
    }
    setEditingField(null);
    setTempValue("");
    setButtonsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const sendMessage = async () => {
    if (!scenario || !scenario?.id) return;
    const res = await manpowerPlanningAI(scenario?.id, false);
    setAIMessage(res);
  };

  useEffect(() => {
    const total = jobs.reduce((sum, job) => sum + (job.numberOfEmployees ?? 0), 0);
    setTotalEmployees(total);

    // calculate total salaries (cost)
    const totalCost = jobs.reduce(
      (sum, job) => sum + (job.salary ?? 0) * (job.numberOfEmployees ?? 0),
      0
    );

    setSpendings(totalCost);

    // subtract from budget
    setBudgetAfterCalculation(scenario.scenarioBudget - totalCost);
  }, [jobs, scenario.scenarioBudget]);

  const handleNodeClick = (_, node) => {
    setSelectedJob(node.data?.manpowerReq); // this gives you the clicked job node data
    setOpenAddJobDialog(true);
  };

  const { nodes, edges, nodeTypes } = useMemo(() => {
    if (!jobs) return { nodes: [], edges: [], nodeTypes: {} };

    const childMap = new Map();
    const nodeMap = new Map();

    // Step 1: Build child map
    jobs.forEach((job) => {
      if (job.reportingToSolid) {
        if (!childMap.has(job.reportingToSolid)) {
          childMap.set(job.reportingToSolid, []);
        }
        childMap.get(job.reportingToSolid).push(job);
      }
    });

    // Step 2: Collect all subtrees
    const collectedJobs = new Map();

    const collectSubtree = (jobId) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return;
      collectedJobs.set(jobId, job);
      const children = childMap.get(jobId) || [];
      children.forEach((child) => collectSubtree(child.id));
    };

    // 👉 Collect *all* roots (reportingToSolid == null)
    const rootJobs = jobs.filter((j) => j.reportingToSolid == null);

    rootJobs.forEach((root) => {
      collectSubtree(root.id);
    });

    // Step 3: Recursive position calculator
    const calculatePositions = (jobId, x, y) => {
      const children = (childMap.get(jobId) || []).filter((c) => collectedJobs.has(c.id));
      const numChildren = children.length;

      if (numChildren === 0) {
        nodeMap.set(jobId, { x, y });
        return { x, width: 180 };
      }

      let totalWidth = 0;
      const childPositions = [];

      children.forEach((child) => {
        const childResult = calculatePositions(child.id, x + totalWidth, y + 165);
        totalWidth += childResult.width + 70;
        childPositions.push(childResult);
      });

      const parentX = x + totalWidth / 2 - 90;
      nodeMap.set(jobId, { x: parentX, y });
      return { x: parentX, width: totalWidth };
    };

    // 👉 Step 4: Layout multiple root trees side by side
    let currentX = 0;
    rootJobs.forEach((root) => {
      const result = calculatePositions(root.id, currentX, 50);
      currentX += result.width + 200; // spacing between root trees
    });

    // Step 5: Generate nodes
    const nodes = Array.from(collectedJobs.values()).map((job) => ({
      id: job.id.toString(),
      type: "customNode",
      data: {
        label: job.jobName,
        manpowerReq: job,
        manpowerReqDataRecieved: true,
        fte: job.numberOfEmployees ?? null,
        totalEmployees,
        salary: job.salary ?? null,
        handleSave: handleAddJob,
        fetchRevenueGenerators,
        reportingToSolid: !!job.reportingToSolid,
        job,
        allJobs: jobs,
        // revenueGenerators: revenueGeneratorsSim,
        // matchingRevenueGenerator: revenueGeneratorsSim?.filter((rg) => rg.jobId === job.id) || [],
        isScenario: true,
        loc: "/manpower/simulation/scenarios",
        scenario,
      },
      position: nodeMap.get(job.id),
      width: 210,
      height: "auto",
      style: {
        cursor: "pointer",
        border: "1px solid #ccc",
        backgroundColor: "#fff",
        width: `210px`,
      },
    }));

    // Step 6: Generate edges
    const edges = Array.from(collectedJobs.values())
      .filter((job) => job.reportingToSolid && collectedJobs.has(job.reportingToSolid))
      .map((job) => ({
        id: `e${job.id}`,
        source: job.reportingToSolid.toString(),
        target: job.id.toString(),
      }));

    const nodeTypes = {
      customNode: CustomJobNode,
    };

    return { nodes, edges, nodeTypes };
  }, [jobs, totalEmployees]);

  // useEffect(() => {
  //   // Use debouncedSearch here, not search
  //   if (!debouncedSearch) {
  //     fitView({ padding: 0.2, duration: 800 });
  //     return;
  //   }

  //   // You don't strictly need the extra setTimeout here anymore,
  //   // but keeping a small one (50ms) ensures the render cycle is complete.
  //   const timer = setTimeout(() => {
  //     const match = nodes.find((node) =>
  //       node.data?.label?.toLowerCase().includes(debouncedSearch.toLowerCase())
  //     );

  //     if (
  //       match &&
  //       match.position &&
  //       typeof match.position.x === "number" &&
  //       typeof match.position.y === "number"
  //     ) {
  //       const x = match.position.x + 105;
  //       const y = match.position.y + 50;

  //       if (!isNaN(x) && !isNaN(y)) {
  //         setCenter(x, y, { zoom: 1.2, duration: 1000 });
  //       }
  //     }
  //   }, 50);

  //   return () => clearTimeout(timer);

  //   // ⚠️ DEPENDENCY CHANGE: Use debouncedSearch
  // }, [debouncedSearch, nodes, setCenter, fitView]);

  useEffect(() => {
    if (!rfInstance || nodes.length === 0 || hasFitViewRun.current) return;

    const t = setTimeout(() => {
      rfInstance.fitView({ padding: 0.2, duration: 800 });
      hasFitViewRun.current = true;
    }, 100);

    return () => clearTimeout(t);
  }, [rfInstance, nodes]);

  useEffect(() => {
    if (!rfInstance) return;

    // 🔁 Search cleared → back to full view
    if (!debouncedSearch) {
      rfInstance.fitView({ padding: 0.2, duration: 800 });
      return;
    }

    const timer = setTimeout(() => {
      const match = nodes.find((node) =>
        node.data?.label?.toLowerCase().includes(debouncedSearch.toLowerCase())
      );

      if (match?.position) {
        rfInstance.setCenter(match.position.x + 105, match.position.y + 50, {
          zoom: 1.2,
          duration: 1000,
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [debouncedSearch, nodes, rfInstance]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Editable Scenario Name */}
      {/* Scenario Name */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {editingField === "scenarioName" ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            variant="outlined"
            size="medium"
            sx={{ flex: 1 }}
          />
        ) : (
          <Typography variant="h4" fontWeight="bold">
            {scenario.scenarioName || "Unnamed Scenario"}
          </Typography>
        )}

        {editingField === "scenarioName" ? (
          <>
            {buttonsLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {" "}
                <IconButton size="small" color="success" onClick={handleSave}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={handleCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </>
        ) : (
          <IconButton
            size="small"
            onClick={() => handleEdit("scenarioName", scenario.scenarioName)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Scenario Budget */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {editingField === "scenarioBudget" ? (
          <TextField
            fullWidth
            margin="normal"
            label={t("user.scenarios.scenarioBudget")}
            type="text"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            value={tempValue === "" ? "" : Number(tempValue).toLocaleString()}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/,/g, "");
              if (rawValue === "" || (/^\d+$/.test(rawValue) && Number(rawValue) >= 0)) {
                setTempValue(rawValue);
              }
            }}
            onKeyDown={handleKeyDown}
            autoFocus
            variant="outlined"
            size="medium"
          />
        ) : (
          <Typography variant="h5" fontWeight="500" color="text.secondary">
            {t("user.scenarios.budget")}:{" "}
            <strong style={{ fontSize: "1.4rem" }}>
              {formatNumber(scenario.scenarioBudget) || "0"}
            </strong>
          </Typography>
        )}

        {editingField === "scenarioBudget" ? (
          <>
            {buttonsLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {" "}
                <IconButton size="small" color="success" onClick={handleSave}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={handleCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </>
        ) : (
          <IconButton
            size="small"
            onClick={() => handleEdit("scenarioBudget", scenario.scenarioBudget)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="500" color="text.secondary">
          {t("user.scenarios.scenarioType")}:{" "}
          <strong style={{ fontSize: "1.2rem" }}>
            {/* {scenario.type || t("user.scenarios.noType")} */}
            {scenario.type
              ? t(`user.scenarios.scenarioTypes.${scenario.type}`)
              : t("user.scenarios.noType")}
          </strong>
        </Typography>
      </Stack>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "5px",
          paddingRight: "20px",
        }}
      >
        <MDButton
          variant="contained"
          color="primary"
          startIcon={<Download size={18} />}
          onClick={async () => {
            const res = await getScenarioOverviewExport(scenario?.id, scenario?.scenarioName);
            if (res === "error") {
              console.error("Export failed");
            }
          }}
          style={{
            marginRight: 5,
          }}
        >
          {t("common.export")}
        </MDButton>
        <AiUpdateButton handleAICall={sendMessage} />
      </div>

      {/* Toolbar: Search, Filter, Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap", // Allows wrapping on small screens
        }}
      >
        {/* 1. Search Input (Smaller width) */}
        <TextField
          label={t("admin.jobs.searchNodes")}
          variant="outlined"
          size="small" // Makes it more compact
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", md: 220 } }} // Fixed width on desktop
        />

        {/* 3. Expand / Collapse All Toggles */}
        <Box sx={{ display: "flex", gap: 1, borderLeft: "1px solid #ccc", pl: 2 }}>
          {/* Optional: Reset Filter Button */}
          {search && (
            <Tooltip title="Reset Filters">
              <IconButton
                onClick={() => {
                  setSearch("");
                }}
                color="error"
                size="small"
              >
                <FilterAltOff />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Jobs Section (small caption instead of full title) */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        {t("user.scenarios.jobsInScenario")}
      </Typography>

      <Box
        sx={{
          height: "70vh",
          border: "1px solid #ccc",
          borderRadius: 2,
          position: "relative",
          overflow: "hidden", // Ensures bubbles don't overflow the container
        }}
      >
        {/* ReactFlow Canvas */}
        {/* <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          fitView
          minZoom={0.1}
        >
          <Background variant="dots" gap={12} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow> */}

        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          onInit={setRfInstance}
          minZoom={0.1}
        />

        {/* Stats Overlay (Your existing code) */}
        <motion.div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            fontSize: "28px",
            fontWeight: "bold",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <span>{t("user.scenarios.unconsumedBudget")}:</span>{" "}
          <span style={{ color: "green" }}>
            <AnimatedNumber value={Number(budgetAfterCalculation)} duration={1} />
          </span>
        </motion.div>

        <motion.div
          style={{
            position: "absolute",
            top: 55,
            left: 10,
            fontSize: "28px",
            fontWeight: "bold",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <span>{t("user.scenarios.spendings")}:</span>{" "}
          <span style={{ color: "red" }}>
            <AnimatedNumber value={Number(spendings)} duration={1} />
          </span>
        </motion.div>

        {/* Add Job Button */}
        <MDButton
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10,
            borderRadius: "20px",
            textTransform: "none",
          }}
          onClick={() => setOpenAddJobDialog(true)}
        >
          {t("user.scenarios.addJob")}
        </MDButton>

        {/* --- AI INSIGHTS OVERLAY --- */}
        {/* Positioned on the right side, below the Add Job button */}
        <Box
          sx={{
            position: "absolute",
            top: 60, // Below the button
            right: 10,
            zIndex: 9, // Below button, above chart
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            maxHeight: "calc(100% - 70px)",
            overflowY: "auto", // Allows scrolling if all bubbles are open and long
            pointerEvents: "none", // Allows clicking through the empty space
          }}
        >
          {/* Only render if aiMessage exists */}
          {aiMessage && (
            <Box sx={{ pointerEvents: "auto" }}>
              {" "}
              {/* Re-enable clicks for the cards */}
              {/* 1. STRENGTHS */}
              {aiMessage.strengths?.length > 0 && (
                <InsightCard
                  title={t("Strengths")}
                  icon={<StrengthIcon color="success" fontSize="small" />}
                  color="#4CAF50" // Success Green
                  visible={visibleBubbles.strengths}
                  onClose={() => handleCloseBubble("strengths")}
                >
                  <RenderBulletList items={aiMessage.strengths} type="strength" t={t} />
                </InsightCard>
              )}
              {/* 2. WEAKNESSES */}
              {aiMessage.weaknesses?.length > 0 && (
                <InsightCard
                  title={t("Weaknesses")}
                  icon={<WeaknessIcon color="error" fontSize="small" />}
                  color="#F44336" // Error Red
                  visible={visibleBubbles.weaknesses}
                  onClose={() => handleCloseBubble("weaknesses")}
                >
                  <RenderBulletList items={aiMessage.weaknesses} type="weakness" t={t} />
                </InsightCard>
              )}
              {/* 3. SUGGESTIONS */}
              {aiMessage.suggestions && (
                <InsightCard
                  title={t("AI Suggestions")}
                  icon={<SuggestionIcon color="info" fontSize="small" />}
                  color="#2196F3" // Info Blue
                  visible={visibleBubbles.suggestions}
                  onClose={() => handleCloseBubble("suggestions")}
                >
                  <RenderRegexContent text={aiMessage.suggestions} t={t} />
                </InsightCard>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <AddScenarioJobDialog
        open={openAddJobDialog}
        onClose={() => {
          setOpenAddJobDialog(false);
          setSelectedJob(null);
        }}
        onSave={handleAddJob}
        scenarioId={scenario.id} // 👈 pass the scenario id
        jobs={
          selectedJob && selectedJob.reportingToSolid == null
            ? []
            : jobs.filter((j) => j.id !== selectedJob?.id)
        }
        job={selectedJob}
        onDelete={handleDeleteJob}
      />
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
// scenario, setScenario, jobs, setJobs

ScenarioPage.propTypes = {
  scenario: PropTypes.array.isRequired,
  setScenario: PropTypes.func.isRequired,
  jobs: PropTypes.array.isRequired,
  setJobs: PropTypes.func.isRequired,
  // revenueGeneratorsSim: PropTypes.array.isRequired,
  setRevenueGeneratorsSim: PropTypes.func.isRequired,
  triggerParentRefresh: PropTypes.func.isRequired,
};
export default ScenarioPage;
